import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getSaveProductCart,
  getItemsForReturn,
  getOrderCancel,
  getOrderDetail,
  reOrder,
  returnItems,
  getReportIssueReason,
} from "../../redux/actions/action-creator";
import "./order.css";
import OrderDetails from "./OrderDetails";
import OrderSummary from "./OrderSummary";
import OrderReviewModal from './OrderReviewModal';
import CancelReasonModal from "./CancelReasonModal";
import ConfirmCancelModal from './ConfirmCancelModal';
import ReportReasonModal from './ReportReasonModal';
import ReturnDetailModal from "./ReturnDetailModal";
import ReturnOrderConfirmModal from "./ReturnOrderConfirmModal";
import { OrderDetailSkeleton, OrdersSkeleton } from "../SkeletonLoader";
import { DEFAULT_DELIVERY_LOCATION } from "../../utils/locationDefaults";

const DEFAULT_CANCEL_LOCATION = DEFAULT_DELIVERY_LOCATION;

const DEFAULT_RETURN_SEGMENT_SLUG = "GROCERY";

const isSuccessResponse = (response) =>
  response?.result === true || response?.result === 1 || response?.result === "1";

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const getPlainObjectPayload = (value) => {
  const parsedValue = parseJsonValue(value);

  if (isPlainObject(parsedValue)) return parsedValue;
  if (Array.isArray(parsedValue)) {
    return parsedValue.map(parseJsonValue).find(isPlainObject) || null;
  }

  return null;
};

const getReOrderSaveCartPayload = (response = {}) => {
  const parsedResponse = parseJsonValue(response);
  const parsedData = parseJsonValue(parsedResponse?.data);
  const possiblePayloads = [
    parsedResponse?.response,
    parsedResponse?.response_data,
    parsedResponse?.cart_payload,
    parsedResponse?.cart_data,
    parsedData?.response,
    parsedData?.response_data,
    parsedData?.cart_payload,
    parsedData?.cart_data,
    parsedData,
  ];

  return possiblePayloads
    .map(getPlainObjectPayload)
    .find(Boolean) || null;
};

const getFirstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    if (!isPlainObject(source)) continue;

    const matchedKey = keys.find((key) => hasValue(source?.[key]));
    if (matchedKey) return source[matchedKey];
  }

  return fallback;
};

const ORDER_RATING_KEYS = [
  "product_quality_rating",
  "product_quality_points",
  "productQualityRating",
  "productQualityPoints",
  "rating",
  "user_rating_points",
  "userRatingPoints",
  "packaging_goods_rating",
  "packaging_goods_points",
  "packagingGoodsRating",
  "packagingGoodsPoints",
  "product_accuracy_rating",
  "product_accuracy_points",
  "productAccuracyRating",
  "productAccuracyPoints",
  "comment",
  "user_comment",
  "rating_image_one",
  "image_one",
];

const hasOrderRatingData = (value) =>
  isPlainObject(value) && ORDER_RATING_KEYS.some((key) => hasValue(value?.[key]));

const getOrderRatingPayloadFromResponse = (response = {}, fallbackRating = {}) => {
  const parsedResponse = parseJsonValue(response);
  const parsedData = parseJsonValue(parsedResponse?.data);
  const parsedResponseData = parseJsonValue(parsedResponse?.response_data);

  const candidates = [
    parsedData?.ratings,
    parsedData?.rating,
    parsedData?.rating_data,
    parsedData?.ratingData,
    parsedData?.order_rating,
    parsedData?.orderRating,
    parsedData?.order_review,
    parsedData?.orderReview,
    parsedData,
    parsedResponseData?.ratings,
    parsedResponseData?.rating,
    parsedResponseData?.rating_data,
    parsedResponseData?.ratingData,
    parsedResponseData?.order_rating,
    parsedResponseData?.orderRating,
    parsedResponseData,
    parsedResponse?.ratings,
    parsedResponse?.rating,
    parsedResponse?.rating_data,
    parsedResponse?.ratingData,
    parsedResponse?.order_rating,
    parsedResponse?.orderRating,
    parsedResponse?.order_review,
    parsedResponse?.orderReview,
  ];

  const responseRating = candidates
    .map(getPlainObjectPayload)
    .find(hasOrderRatingData);

  return {
    ...fallbackRating,
    ...(responseRating || {}),
  };
};

const normalizeReOrderSaveCartPayload = ({
  payload = {},
  ordersDetail = {},
  location = {},
}) => {
  const receipt = parseJsonValue(ordersDetail?.receipt);
  const source = isPlainObject(payload) ? payload : {};
  const sources = [source, ordersDetail, isPlainObject(receipt) ? receipt : {}, location];

  return {
    ...source,
    business_segment_id: getFirstValue(sources, [
      "business_segment_id",
      "businessSegmentId",
      "business_segment",
      "businessSegment",
      "store_id",
      "storeId",
      "branch_id",
    ]),
    cart_id: getFirstValue(sources, ["cart_id", "cartId", "id"]),
    empty_bottle_count: getFirstValue(sources, ["empty_bottle_count", "emptyBottleCount"], ""),
    home_or_office: getFirstValue(sources, ["home_or_office", "homeOrOffice"], 0),
    latitude: getFirstValue(sources, ["latitude", "lat"], location.latitude),
    longitude: getFirstValue(sources, ["longitude", "lng", "long"], location.longitude),
    product_details: getFirstValue(sources, ["product_details", "product_detail"], ""),
    product_update: getFirstValue(sources, ["product_update", "productUpdate"], "NO"),
    product_variant_id: getFirstValue(sources, [
      "product_variant_id",
      "productVariantId",
      "variant_id",
      "variantId",
    ], 0),
    portion: getFirstValue(sources, [
      "portion",
      "product_portion",
      "productPortion",
      "selected_portion",
      "selectedPortion",
      "portion_name",
      "portionName",
    ], ""),
    quantity: getFirstValue(sources, ["quantity", "qty"], 0),
    reward_points: getFirstValue(sources, ["reward_points", "rewardPoints"], 0),
    segment_id: getFirstValue(sources, ["segment_id", "segmentId", "segment"], 4),
    single_product: getFirstValue(sources, ["single_product", "singleProduct"], "NO"),
    merchant_id: getFirstValue(sources, ["merchant_id", "merchantId"], 1),
    area: getFirstValue(sources, ["area", "area_id", "country_area_id"], ""),
    "for": getFirstValue(sources, ["for", "cart_for", "cartFor"], 2),
    distance: getFirstValue(sources, ["distance", "store_distance", "storeDistance"], ""),
    cart_amount: getFirstValue(sources, [
      "cart_amount",
      "cartAmount",
      "item_total",
      "sub_total",
      "subtotal",
      "total_amount",
    ], ""),
  };
};

const getReturnProductsFromResponse = (response = {}) => {
  const data = response?.data;
  const productData = data?.product_data || {};

  if (Array.isArray(data)) return data;

  const possibleLists = [
    data?.products,
    data?.product,
    data?.items,
    data?.return_items,
    data?.return_request_items,
    data?.product_data,
    productData?.products,
    productData?.items,
    productData?.confirmed_items,
    productData?.pending_items,
    productData?.substitute_items,
  ];

  const directList = possibleLists.find((list) => Array.isArray(list));
  if (directList) return directList;

  if (data && typeof data === "object") {
    const nestedList = Object.values(data).find((value) => Array.isArray(value));
    return nestedList || [];
  }

  return [];
};

const getSavedLocation = () => {
  try {
    const savedLocation = localStorage.getItem("user_location");
    return savedLocation ? JSON.parse(savedLocation) : {};
  } catch (error) {
    return {};
  }
};

const Orders = ({
  orders,
  loading = false,
  onOrdersRefresh,
  onLoadMoreOrders,
  loadingMore = false,
  hasMoreOrders = false,
  segmentId,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orderItems = useMemo(() => (Array.isArray(orders) ? orders : []), [orders]);
  const [showReview, setShowReview] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showReportReason, setShowReportReason] = useState(false);
  const [showReturnConfirm, setShowReturnConfirm] = useState(false);
  const [showReturnDetail, setShowReturnDetail] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const {ordersDetail = {}} = useSelector((state) => state.common);
  const authLocation = useSelector((state) => ({
    latitude: state.auth?.latitude,
    longitude: state.auth?.longitude,
    user_location: state.auth?.user_location,
  }));

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [returned, setReturned] = useState(false);
  const [returnItemsList, setReturnItemsList] = useState([]);
  const [returnItemsLoading, setReturnItemsLoading] = useState(false);
  const [returnItemsError, setReturnItemsError] = useState("");
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [reOrderSubmitting, setReOrderSubmitting] = useState(false);
  const [issueList, setIssueList] = useState([]);
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueFetchError, setIssueFetchError] = useState("");
  const [liveOrdersDetail, setLiveOrdersDetail] = useState({});

  useEffect(() => {
    setLiveOrdersDetail(isPlainObject(ordersDetail) ? ordersDetail : {});
  }, [ordersDetail]);

  const visibleOrdersDetail =
    isPlainObject(liveOrdersDetail) && Object.keys(liveOrdersDetail).length > 0
      ? liveOrdersDetail
      : ordersDetail;
  const orderCurrency =
    visibleOrdersDetail?.currency ||
    visibleOrdersDetail?.currency_code ||
    visibleOrdersDetail?.currencyCode ||
    "AED";

  const loadOrderDetail = useCallback(async (orderId) => {
    if (!orderId) return;

    setSelectedOrder(orderId);
    setDetailLoading(true);

    let formData = new FormData();
    formData.append("order_id", orderId);

    try {
      await dispatch(getOrderDetail(formData));
    } catch (error) {
      console.log("Order detail API Error:", error?.message);
    } finally {
      setDetailLoading(false);
    }
  }, [dispatch]);

    // ✅ API CALL (service + tab change pe)
  useEffect(() => {
    if (loading) return;

    if (!orderItems.length) {
      setSelectedOrder(null);
      return;
    }

    const selectedOrderExists =
      selectedOrder &&
      orderItems.some((item) => String(item?.order_id) === String(selectedOrder));

    if (selectedOrderExists) return;

    const firstOrderId = orderItems[0]?.order_id;
    if (firstOrderId) {
      loadOrderDetail(firstOrderId);
    }
  }, [loading, loadOrderDetail, orderItems, selectedOrder]);

  const extractIssueReasons = (response) => {
    if (!response || typeof response !== "object") {
      return [];
    }

    const issueSources = [
      response?.issue_list,
      response?.issues,
      response?.data,
      response?.data?.issue_list,
      response?.response_data,
      response?.response_data?.issue_list,
    ];

    return issueSources.find(Array.isArray) || [];
  };

  const handleOrderClick = (orderId) => {
    loadOrderDetail(orderId);
  };

  const getOrderStatusClassName = (item) => {
    const statusText = String(item?.order_status || item?.status || "").toLowerCase();

    if (statusText.includes("cancel")) return "text-danger order-list-status-cancelled";
    if (statusText.includes("deliver") || statusText.includes("complete")) {
      return "text-success order-list-status-delivered";
    }

    return "text-primary";
  };

  const getOrderStatusIconClassName = (item) => {
    const statusText = String(item?.order_status || item?.status || "").toLowerCase();

    if (statusText.includes("cancel")) return "ph-x";
    if (statusText.includes("deliver") || statusText.includes("complete")) return "ph-checks";

    return "ph-truck";
  };

  const handleOpenReportReason = async () => {
    setIssueLoading(true);
    setIssueFetchError("");
    setIssueList([]);

    const formData = new FormData();
    formData.append("segment_slug", "GROCERY");
    formData.append("merchant_id", 1);

    try {
      const response = await dispatch(getReportIssueReason(formData));
      const reasons = extractIssueReasons(response);

      if (reasons.length > 0) {
        setIssueList(reasons);
      } else {
        setIssueList(Array.isArray(ordersDetail?.issue_list) ? ordersDetail.issue_list : []);
      }
    } catch (error) {
      setIssueFetchError(
        error?.message || error?.data?.message || "Unable to load report issue reasons."
      );
      setIssueList(Array.isArray(ordersDetail?.issue_list) ? ordersDetail.issue_list : []);
    } finally {
      setIssueLoading(false);
      setShowReportReason(true);
    }
  };

  const handleOrdersScroll = (event) => {
    if (!onLoadMoreOrders || loadingMore || !hasMoreOrders) return;

    const target = event.currentTarget;
    const canScrollVertical = target.scrollHeight > target.clientHeight + 4;
    const canScrollHorizontal = target.scrollWidth > target.clientWidth + 4;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 40;
    const nearRight = target.scrollLeft + target.clientWidth >= target.scrollWidth - 40;

    if ((canScrollVertical && nearBottom) || (canScrollHorizontal && nearRight)) {
      onLoadMoreOrders();
    }
  };

  const handleReportIssueSuccess = async () => {
    if (onOrdersRefresh) {
      await onOrdersRefresh();
    }
    if (selectedOrder) {
      await loadOrderDetail(selectedOrder);
    }
  };

  const getActiveLocation = () => {
    const savedLocation = getSavedLocation();
    const reduxLocation = authLocation.user_location || {};

    return {
      latitude:
        authLocation.latitude ||
        reduxLocation.latitude ||
        savedLocation.latitude ||
        DEFAULT_CANCEL_LOCATION.latitude,
      longitude:
        authLocation.longitude ||
        reduxLocation.longitude ||
        savedLocation.longitude ||
        DEFAULT_CANCEL_LOCATION.longitude,
      merchant_id:
        reduxLocation.merchant_id ||
        reduxLocation.merchantId ||
        savedLocation.merchant_id ||
        savedLocation.merchantId ||
        1,
      area:
        reduxLocation.area ||
        reduxLocation.area_id ||
        savedLocation.area ||
        savedLocation.area_id ||
        "",
    };
  };

  const getCancelSegmentId = () =>
    segmentId ||
    ordersDetail?.segment_id ||
    ordersDetail?.segmentId ||
    ordersDetail?.business_segment_id ||
    ordersDetail?.businessSegmentId ||
    "";

  const getReturnSegmentSlug = () => {
    const segmentSlug =
      ordersDetail?.segment_slug ||
      ordersDetail?.segmentSlug ||
      ordersDetail?.business_segment_slug ||
      ordersDetail?.businessSegmentSlug ||
      DEFAULT_RETURN_SEGMENT_SLUG;

    return String(segmentSlug).toUpperCase();
  };

  const getReturnBookingOrderId = () =>
    ordersDetail?.booking_order_id ||
    ordersDetail?.bookingOrderId ||
    ordersDetail?.order_id ||
    ordersDetail?.id ||
    selectedOrder ||
    "";

  const handleCancelSubmit = async (cancelReasonId) => {
    if (!ordersDetail?.order_id || !cancelReasonId || cancelSubmitting) return;

    const location = getActiveLocation();
    const payload = new URLSearchParams();
    payload.append("order_id", ordersDetail.order_id);
    payload.append("latitude", location.latitude);
    payload.append("longitude", location.longitude);
    payload.append("cancel_reason_id", cancelReasonId);
    payload.append("user_type", "B2C");

    setCancelSubmitting(true);

    try {
      const response = await dispatch(getOrderCancel(payload));
      if (response?.result === true || response?.result === 1 || response?.result === "1") {
        setShowReason(false);
        if (onOrdersRefresh) {
          await onOrdersRefresh();
        }
      }
    } catch (error) {
      console.log("Order cancel API Error:", error?.message);
    } finally {
      setCancelSubmitting(false);
    }
  };

  // Handler function
  const handleWriteReview = () => {
    setShowReview(true);
  };

  const handleOrderReviewSuccess = ({ response, ratingData } = {}) => {
    const nextRatings = getOrderRatingPayloadFromResponse(response, ratingData);

    setLiveOrdersDetail((previousDetail) => {
      const baseDetail =
        isPlainObject(previousDetail) && Object.keys(previousDetail).length > 0
          ? previousDetail
          : ordersDetail;

      return {
        ...baseDetail,
        ratings: nextRatings,
      };
    });
  };

  const handleReOrder = async () => {
    const bookingOrderId = getReturnBookingOrderId();

    if (!bookingOrderId || reOrderSubmitting) return;

    const payload = new URLSearchParams();
    payload.append("segment_slug", getReturnSegmentSlug());
    payload.append("booking_order_id", bookingOrderId);

    setReOrderSubmitting(true);

    try {
      const response = await dispatch(reOrder(payload));

      if (!isSuccessResponse(response)) return;

      const saveCartPayload = getReOrderSaveCartPayload(response);

      if (!saveCartPayload) {
        console.log("Re-order save cart payload not found:", response);
        return;
      }

      const saveCartResponse = await dispatch(
        getSaveProductCart(
          normalizeReOrderSaveCartPayload({
            payload: saveCartPayload,
            ordersDetail,
            location: getActiveLocation(),
          })
        )
      );

      if (isSuccessResponse(saveCartResponse)) {
        navigate("/cart", { replace: true });
      }
    } catch (error) {
      console.log(
        "Re-order API Error:",
        error?.response?.data?.message || error?.message || error
      );
    } finally {
      setReOrderSubmitting(false);
    }
  };

  const loadReturnItems = async () => {
    const bookingOrderId = getReturnBookingOrderId();

    setReturnItemsList([]);
    setReturnItemsError("");

    if (!bookingOrderId) {
      setReturnItemsError("Order id not found.");
      return;
    }

    const payload = new URLSearchParams();
    payload.append("segment_slug", getReturnSegmentSlug());
    payload.append("booking_order_id", bookingOrderId);

    setReturnItemsLoading(true);

    try {
      const response = await dispatch(getItemsForReturn(payload));
      setReturnItemsList(getReturnProductsFromResponse(response));
    } catch (error) {
      setReturnItemsError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load return items."
      );
    } finally {
      setReturnItemsLoading(false);
    }
  };

  const handleReturnContinue = () => {
    setShowReturnConfirm(false);
    setShowReturnDetail(true);
    loadReturnItems();
  };

  const handleReturnSubmit = async (returnData) => {
    const bookingOrderId = getReturnBookingOrderId();
    const selectedReturnItems = Array.isArray(returnData?.selectedItems)
      ? returnData.selectedItems
      : [];

    if (!bookingOrderId || !selectedReturnItems.length || returnSubmitting) return;

    const payload = new FormData();
    payload.append("segment_slug", getReturnSegmentSlug());
    payload.append("booking_order_id", bookingOrderId);
    payload.append("return_request_items", JSON.stringify(selectedReturnItems));

    (returnData?.images || []).forEach((file, index) => {
      if (file) {
        payload.append(`return_image_${index + 1}`, file);
      }
    });

    setReturnSubmitting(true);
    setReturnItemsError("");

    try {
      const response = await dispatch(returnItems(payload));
      if (isSuccessResponse(response)) {
        setReturned(true);
        setShowReturnDetail(false);
        if (onOrdersRefresh) {
          await onOrdersRefresh();
        }
        if (selectedOrder) {
          await loadOrderDetail(selectedOrder);
        }
      }
    } catch (error) {
      setReturnItemsError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to submit return request."
      );
    } finally {
      setReturnSubmitting(false);
    }
  };
  if (loading) {
    return <OrdersSkeleton />;
  }

  if (!orderItems.length) {
    return (
      <div className="container container-lg bg-light py-24 orders-page-container">
        <div className="card p-24 text-center orders-empty-card">
          <h6 className="mb-8">No orders found</h6>
          <p className="mb-0 text-gray-500">Your orders will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="container container-lg bg-light py-24 orders-page-container">
      <div className="row g-3 orders-layout-row">

        {/* LEFT PANEL */}
        <div className="col-xl-3 col-lg-4 col-12 order-0">
          <div className="card h-100 mobile-orders orders-sidebar-card">
            <div className="orders-history-title">
              Orders History
            </div>

            <div
              className="list-group list-group-flush orders-list orders-scroll"
              onScroll={handleOrdersScroll}
            >
              {orderItems.map((item, i) => (
                <div
                  key={item?.order_id || item?.order_no || i}
                  onClick={() => handleOrderClick(item.order_id)}
                  className={`list-group-item order-item order-pill ${item.order_id === selectedOrder ? "active" : ""}`}
                >
                  <div className="order-list-main">
                    <div className="order-list-copy">
                      <div className="fw-semibold order-list-id">#{item.order_no}</div>
                      <span className="order-list-amount">{item.currency} {item.total_amount}</span>
                      <small className="order-list-items">{item.total_items} Items</small>
                    </div>

                    <div className="order-list-side">
                      <small className={`order-list-status ${getOrderStatusClassName(item)}`}>
                        <i
                          className={`ph ${getOrderStatusIconClassName(item)} order-list-status-icon`}
                          aria-hidden="true"
                        />
                        <span>{item.order_status}</span>
                      </small>

                      <div className="order-list-progress" aria-hidden="true">
                        <span className="order-list-progress-dot" />
                        <span className="order-list-progress-line" />
                        <span className="order-list-progress-dot" />
                        <span className="order-list-progress-line" />
                        <span className="order-list-progress-dot" />
                        <span className="order-list-progress-line" />
                        <span className="order-list-progress-dot" />
                        <span className="order-list-progress-line" />
                        <span className="order-list-progress-dot" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {loadingMore && (
                <div className="orders-load-more-state">Loading more orders...</div>
              )}
              {!loadingMore && !hasMoreOrders && orderItems.length > 0 && (
                <div className="orders-load-more-state">No more orders</div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-xl-9 col-lg-8 col-12 order-1">
          <div className="orders-detail-panel">
          {detailLoading ? (
            <OrderDetailSkeleton />
          ) : (
          <>
          <div className="order-card py-20 mb-10">

            {/* Order ID */}
            <div className="order-id">Order #{ordersDetail?.order_no}</div>

            <div className="order-address-route">
              {/* Pickup address */}
              <div className="address-row address-row-start">
                <i className="ph ph-map-pin address-location-icon address-location-start" aria-hidden="true" />
                <span>{ordersDetail?.pickup}</span>
              </div>

              {/* Delivery address */}
              <div className="address-row address-row-end">
                <i className="ph ph-map-pin address-location-icon address-location-end" aria-hidden="true" />
                <span>{ordersDetail?.drop_off}</span>
              </div>
            </div>

            <hr className="divider" />
            {/* FOOTER ACTIONS */}
            {ordersDetail.order_status !== "Order Delivered" && (
              <div className="summary-footer">
                <div className="order-actions">
                  <small className="text-danger">
                      If you choose to cancel, you can do it within 10 minutes.
                      A {orderCurrency} 5.00 cancellation fee may apply.
                  </small>
                  <button
                    className="btn-outline order-footer-action-btn"
                    onClick={() => setShowConfirm(true)}
                  >
                    <i className="ph ph-x" aria-hidden="true" />
                    <span>Cancel</span>
                  </button>
                  <button
                    className="btn-outline order-footer-action-btn"
                    onClick={handleOpenReportReason}
                  >
                    <i className="ph ph-info" aria-hidden="true" />
                    <span>Report Issue</span>
                  </button>
                  {/*<button className="btn-outline">Re-Order</button>*/}
                </div>
              </div> 
            )}
              
            {/* Action Buttons */}
            {ordersDetail.order_status === "Order Delivered" && (
              <div className="buttons-row">
                {/* Report Issue */}
                <button className="action-btn" onClick={handleOpenReportReason}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Report Issue
                </button>

                {/* Re-Order */}
                <button
                  className="action-btn"
                  onClick={handleReOrder}
                  disabled={reOrderSubmitting}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10"/>
                    <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                  </svg>
                  {reOrderSubmitting ? "Re-Ordering..." : "Re-Order"}
                </button>

                {/* Order Rating */}
                <button className="action-btn" onClick={() => handleWriteReview()}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  Order Rating
                </button>

                {/* Return */}
                <button
                  className={`action-btn btn-return ${returned ? "returned" : ""}`}
                  onClick={() => setShowReturnConfirm(true)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 14 4 9 9 4"/>
                    <path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
                  </svg>
                  {returned ? "Returned!" : "Return"}
                </button>
              </div>
            )}  
          </div>
          <OrderDetails ordersDetail={ordersDetail}/>
          <OrderSummary ordersDetail={visibleOrdersDetail}/>
          </>
          )}
          </div>
        </div>
      </div>
    </div>
    <OrderReviewModal
      show={showReview}
      onClose={() => { setShowReview(false);}}
      ordersDetail={visibleOrdersDetail}
      onSubmitSuccess={handleOrderReviewSuccess}
    />

    {/* STEP 1: CONFIRM MODAL */}
    <ConfirmCancelModal
      show={showConfirm}
      onYes={() => {
        setShowConfirm(false);   // close confirm
        setShowReason(true);     // open reason
      }}
      onNo={() => setShowConfirm(false)}
    />

    {/* STEP 2: REASON MODAL */}
    <CancelReasonModal
      show={showReason}
      onClose={() => setShowReason(false)}
      onSubmit={handleCancelSubmit}
      orderId={ordersDetail?.order_id}
      segmentId={getCancelSegmentId()}
      submitting={cancelSubmitting}
    />

    <ReportReasonModal
      show={showReportReason}
      onClose={() => setShowReportReason(false)}
      onNo={() => setShowConfirm(false)}
      ordersDetail={ordersDetail}
      onSubmitSuccess={handleReportIssueSuccess}
      issueList={issueList}
      issueLoading={issueLoading}
      issueFetchError={issueFetchError}
    />

    <ReturnOrderConfirmModal
      show={showReturnConfirm}
      onClose={() => setShowReturnConfirm(false)}
      onContinue={handleReturnContinue}
    />

    <ReturnDetailModal
      show={showReturnDetail}
      onClose={() => setShowReturnDetail(false)}
      onSubmit={handleReturnSubmit}
      ordersDetail={ordersDetail}
      returnItems={returnItemsList}
      loading={returnItemsLoading}
      error={returnItemsError}
      submitting={returnSubmitting}
    />
  </>
  );
};

export default Orders;
